import ShellyScriptHightlight from './ShellyScriptHightlight';
import ShellyScriptEditorHistory from './pluging/ShellyScriptEditorHistory';
import ShellyScriptAutoComplete from './pluging/ShellyScriptAutoComplete';
import './css/ScriptsEditor.css';
import { tick } from 'svelte';

export default class ShellyScriptEditor {
	private _editor: HTMLElement;
	private _textEditor: HTMLTextAreaElement;
	private _lines: HTMLElement;
	private _preCode: HTMLElement;
	private _codeWrapper: HTMLElement;
	private _updateCallBack: Function;
	private _code: string = '';
	private _tabSpace: string = '  ';
	private _history: ShellyScriptEditorHistory;
	private _newLine: string = '\n';
	private _autoComplete: ShellyScriptAutoComplete;
	constructor(container: HTMLElement) {
		this._init(container);
		this._history = new ShellyScriptEditorHistory(this._textEditor);
		this._autoComplete = new ShellyScriptAutoComplete(this._textEditor);
		this._autoComplete.addEventListener('clickItem', this._onAutoComplete);
	}
	public updateCode(code: string) {
		this._code = code.trim().replace(/\r(\n)?/g, this._newLine);
		this._textEditor.value = this._code;
		this._history.reset();
		this._update();
		setTimeout(() => this._textEditor.focus(), 0);
	}
	public insertScript = (code: string) => {
		let insertCode = code.replace(/\r(\n)?/g, this._newLine);
		const position = this._textEditor.selectionStart,
			value = this._textEditor.value,
			lastLineStart = Math.max(0, value.slice(0, position).lastIndexOf(this._newLine)),
			lastLine = value.slice(lastLineStart, position),
			indentation = lastLine.split(/[^\s+]/)[0];
		if (indentation) {
			insertCode = code.replace(/\n$/, '');
			const lines = insertCode
				.split(this._newLine)
				.map((i: string) => indentation.replace(this._newLine, '') + i);
			insertCode = this._newLine + lines.join(this._newLine);
		}
		this._setRangeText(insertCode);
		this._updateCode();
	};
	public onUpdate(callback: Function) {
		this._updateCallBack = callback;
	}
	public getCode() {
		return this._code;
	}
	public destroy() {
		this._textEditor.removeEventListener('scroll', this._onScroll);
		this._textEditor.removeEventListener('input', this._onInputCode);
		this._textEditor.removeEventListener('keydown', this._handleKeydown);
		this._autoComplete.removeEventListener('clickItem', this._onAutoComplete);
		this._autoComplete.destroy();
		this._history.destroy();
	}
	private _init(container: HTMLElement) {
		this._editor = document.createElement('div');
		this._editor.className = 'shelly-code-editor';
		this._lines = document.createElement('div');
		this._lines.className = 'line-numbers';
		this._editor.append(this._lines);
		const editor = document.createElement('div');
		editor.className = 'editor';
		this._editor.append(editor);
		this._textEditor = document.createElement('textarea');
		const attrs = {
			maxlength: '50000',
			tabindex: '0',
			wrap: 'off',
			autocorrect: 'off',
			autocapitalize: 'off',
			autocomplete: 'off',
			spellcheck: 'false',
			'aria-roledescription': 'editor',
			'aria-multiline': 'true',
			'aria-haspopup': 'false',
			'aria-autocomplete': 'both',
		};
		for (const attr in attrs) {
			this._textEditor.setAttribute(attr, attrs[attr]);
		}
		editor.append(this._textEditor);
		this._preCode = document.createElement('pre');
		this._codeWrapper = document.createElement('code');
		this._preCode.append(this._codeWrapper);
		editor.append(this._preCode);
		this._editor.append(editor);
		container.prepend(this._editor);
		this._textEditor.addEventListener('scroll', this._onScroll);
		this._textEditor.addEventListener('input', this._onInputCode);
		this._textEditor.addEventListener('keydown', this._handleKeydown);
		this._onScroll();
	}
	private _updateCode() {
		this._code = this._textEditor.value;
		this._update();
	}
	private _onScroll = () => {
		const { scrollLeft, scrollTop } = this._textEditor;
		this._preCode.style.transform = `translate(${scrollLeft * -1}px, ${scrollTop * -1}px)`;
		this._lines.style.transform = `translateY(${scrollTop * -1}px)`;
		if (this._autoComplete) this._autoComplete.setPosition(scrollLeft, scrollTop);
	};
	private _updateLines = async () => {
		let numberList = '';
		this._code.split(this._newLine).forEach((_e, i) => {
			numberList += `<span>${i + 1}</span>`;
		});
		this._lines.innerHTML = numberList;
		// Ivo: doing this so we can wait full content to be rendered before getting scrollHeight
		await tick();
		this._lines.style.height = `${this._textEditor.scrollHeight + 10}px`;
	};
	private _update() {
		this._updateLines();
		this._preCode.style.height = `${this._textEditor.scrollHeight}px`;
		this._codeWrapper.innerHTML = ShellyScriptHightlight.highlight(this._code);
		if (this._updateCallBack) this._updateCallBack(this._code);
	}
	private _onInputCode = (e: InputEvent) => {
		this._code = e.target['value'];
		this._update();
	};
	private _handleKeydown = (e: KeyboardEvent) => {
		if (e.key == 'Tab') {
			this._handleTabs(e);
		} else if (e.key == 'Enter') {
			this._newLineIndentation(e);
		} else if (this._history && e.ctrlKey && /^(z|y)$/.test(e.key)) {
			e.preventDefault();
			e.key == 'z' ? this._history.undo() : this._history.redo();
		} else if (e.ctrlKey && e.key == '/') {
			this._handleComments(e);
		} else this._closingCharacters(e);
	};
	private _handleTabs(e: KeyboardEvent) {
		e.preventDefault();
		let selectionStart = this._textEditor.selectionStart,
			selectionEnd = this._textEditor.selectionEnd,
			tabSpace = this._tabSpace,
			selectionMode: SelectionMode = 'end';
		if (e.shiftKey || selectionStart != selectionEnd) {
			const value = this._textEditor.value,
				initCode = value.slice(selectionStart, selectionEnd),
				beforeSelection = value.slice(0, selectionStart),
				prevLineEnd = beforeSelection.lastIndexOf(this._newLine);
			let code = initCode,
				lines: string[] = [];
			selectionMode = selectionStart != selectionEnd ? 'select' : 'end';
			if (e.shiftKey) {
				selectionStart = prevLineEnd >= 0 ? prevLineEnd + 1 : 0;
				code = value.slice(selectionStart, selectionEnd);
				lines = code
					.split(this._newLine)
					.map((i: string) => i.replace(new RegExp(`^(${this._tabSpace}|\\t)`), ''));
				code = lines.join(this._newLine);
				if (code == initCode) return;
			} else {
				const nextLineIndex = value.slice(selectionEnd).indexOf(this._newLine);
				if (
					selectionStart - 1 == prevLineEnd &&
					(selectionEnd == value.length || nextLineIndex >= 0)
				) {
					lines = code.split(this._newLine).map((i: string) => this._tabSpace + i);
					code = lines.join(this._newLine);
				}
			}
			tabSpace = code;
		}
		this._setRangeText(tabSpace, selectionMode, selectionStart);
		this._updateCode();
	}
	private _handleComments(e: KeyboardEvent) {
		e.preventDefault();
		const code = this._textEditor.value,
			selectionStart = this._textEditor.selectionStart,
			prevLineEnd = code.slice(0, selectionStart).lastIndexOf(this._newLine),
			lineStart = Math.max(0, prevLineEnd) + (prevLineEnd >= 0 ? 1 : 0);
		let lines = code.slice(lineStart, this._textEditor.selectionEnd).split(this._newLine),
			selectedLines = 0,
			commentedLines = 0;
		lines.forEach((i) => {
			if (i.replace(/\/s+/, '').length) {
				selectedLines += 1;
				if (/^\s*\/\/\w*/.test(i)) commentedLines += 1;
			}
		});
		lines = lines.map((i: string) => {
			if (i.replace(/\/s+/, '').length == 0) {
				return i;
			} else if (commentedLines == selectedLines && /^\s*\/\/\w*/.test(i)) {
				return i.replace(/\/\/\s?/, '');
			}
			const m = i.match(/^(\s*)(.*)/);
			return m ? `${m[1]}// ${m[2] || ''}` : `// ${i}`;
		});
		this._setRangeText(lines.join(this._newLine), lines.length > 1 ? 'preserve' : 'end', lineStart);
		this._updateCode();
	}
	private _closingCharacters(e: KeyboardEvent) {
		const openChars = ['(', '[', '{', '"', `'`],
			closeChars = [')', ']', '}', '"', `'`],
			openIndexChar = openChars.indexOf(e.key),
			closeIndexChar = closeChars.indexOf(e.key),
			selectionStart = this._textEditor.selectionStart,
			selectionEnd = this._textEditor.selectionEnd;
		if (selectionStart == selectionEnd && !this._textEditor.value[selectionStart]?.match(/\s/))
			return;
		if (openIndexChar >= 0) {
			e.preventDefault();
			const selectedText =
				e.key +
				this._textEditor.value.slice(selectionStart, selectionEnd) +
				closeChars[openIndexChar];
			this._setRangeText(selectedText);
			this._textEditor.selectionStart = this._textEditor.selectionEnd =
				selectionStart + selectedText.length - 1;
			this._updateCode();
		} else if (
			selectionStart == selectionEnd &&
			closeIndexChar >= 0 &&
			this._textEditor.value[selectionStart] == closeChars[closeIndexChar] &&
			this._textEditor.value[selectionStart - 1] == openChars[closeIndexChar]
		) {
			this._textEditor.selectionStart += 1;
			e.preventDefault();
		}
	}
	private _newLineIndentation(e: KeyboardEvent) {
		const code = this._textEditor.value,
			selectionStart = this._textEditor.selectionStart,
			lastLineStart = Math.max(0, code.slice(0, selectionStart).lastIndexOf(this._newLine)),
			lastLine = code.slice(lastLineStart, selectionStart),
			lastLineBreaks = lastLine.split(/[^\s+]/)[0],
			indentation = (lastLineStart == 0 ? this._newLine : lastLineBreaks) || this._newLine;
		if (this._textEditor.selectionEnd == code.length) return;
		e.preventDefault();
		let newLineMustashe = false,
			newIndentation = indentation;
		if (
			lastLine
				.replace(/(\/\/.*)$/, '')
				.trim()
				.endsWith('{')
		) {
			newIndentation += this._tabSpace;
			if (code[this._textEditor.selectionEnd] == '}') {
				newLineMustashe = true;
				newIndentation += this._newLine + indentation.replace(this._newLine, '');
			}
		}
		this._setRangeText(newIndentation, 'end');
		if (newLineMustashe) {
			this._textEditor.selectionStart = this._textEditor.selectionEnd =
				selectionStart + this._newLine.length + indentation.length + this._tabSpace.length - 1;
		}
		this._updateCode();
	}
	private _setRangeText(
		text: string,
		selectionMode: SelectionMode = 'start',
		selectionStart: number = null,
	) {
		selectionStart = selectionStart !== null ? selectionStart : this._textEditor.selectionStart;
		this._textEditor.dispatchEvent(
			new CustomEvent('beforeinput', {
				detail: { text, selectionStart, inputType: selectionMode },
			}),
		);
		this._textEditor.setRangeText(
			text,
			selectionStart,
			this._textEditor.selectionEnd,
			selectionMode,
		);
	}
	private _onAutoComplete = (e: CustomEvent) => {
		const text = e.detail.text;
		this._setRangeText(text, 'end', e.detail.index);
		this._updateCode();
		if (text.endsWith('}') || text.endsWith(')')) {
			const selectionEnd = this._textEditor.selectionEnd;
			this._textEditor.selectionEnd = selectionEnd - (text.endsWith('}') ? 2 : 1);
		}
	};
}
